import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ClipboardCheck, ArrowLeft, Save, CheckCircle, XCircle, 
  AlertTriangle, History, User, Calendar, Info, Upload, 
  Image, X, MessageSquare, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from '@/services/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

// Mapping des libellés pour les éléments
const ELEMENT_LABELS = {
  "VITESSE_LIMITEE": "Vitesse limitée",
  "FEUX_ROUTE": "Feux de route",
  "PROJECTEURS_LED": "Projecteurs LED",
  "RETROVISEUR_HALO": "Rétroviseur halo 180°",
  "GYROPHARE": "Gyrophare",
  "BANDE_ROUGE": "Bande rouge de signalisation",
  "FEU_RECUL": "Feu de recul",
  "SPOT_BLEU_AVANT": "Spot bleu avant",
  "SPOT_BLEU_ARRIERE": "Spot bleu arrière",
  "TRIANGLE_SIGNALISATION": "Triangle signalisation",
  "CAMERAS": "Caméras",
  "FOURCHES_LASER": "Fourches laser",
  "CLAVIER_AUTORISATION": "Clavier autorisation",
  "PROTECTION_PIEDS": "Protection pieds",
  "GACHETTE_SECURITE": "Gâchette sécurité",
  "CEINTURES_SECURITE": "Ceintures de sécurité",
  "BLOCAGE_CEINTURE": "Blocage ceinture",
  "EXTINCTEUR": "Extincteur",
  "TELEMETRIE": "Télémétrie"
};

export default function MaterielChecklist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser: user } = useApp();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [materiel, setMateriel] = useState(null);
  const [typeChecklist, setTypeChecklist] = useState(null);
  const [checklistRemplie, setChecklistRemplie] = useState(null);
  const [checklistElements, setChecklistElements] = useState([]);
  const [formData, setFormData] = useState({});
  const [historique, setHistorique] = useState([]);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentUploadCode, setCurrentUploadCode] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [checklistData, elementsData, materiels] = await Promise.all([
        api.getMaterielChecklist(id),
        api.getChecklistElements(),
        api.getMateriel()
      ]);

      const currentMateriel = materiels.find(m => m.id === id);
      setMateriel(currentMateriel);
      setTypeChecklist(checklistData.type_checklist);
      setChecklistRemplie(checklistData.checklist_remplie);
      setChecklistElements(elementsData || []);

      // Initialize form data
      const initialFormData = {};
      const typeItems = checklistData.type_checklist?.checklist_items || [];
      const remplieItems = checklistData.checklist_remplie?.checklist_items || [];

      typeItems.forEach(item => {
        const existingItem = remplieItems.find(r => r.code === item.code);
        initialFormData[item.code] = {
          conforme: existingItem?.conforme ?? null,
          commentaire: existingItem?.commentaire || '',
          valeur: existingItem?.valeur || item.valeur || '',
          valeur_interieur: existingItem?.valeur_interieur || item.valeur_interieur || '',
          valeur_exterieur: existingItem?.valeur_exterieur || item.valeur_exterieur || '',
          images: existingItem?.images || []
        };
      });

      setFormData(initialFormData);

      // Load historique
      const histData = await api.getMaterielChecklistHistorique(id).catch(() => ({ historique: [] }));
      setHistorique(histData.historique || []);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleConformeChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      [code]: { ...prev[code], conforme: value }
    }));
  };

  const handleCommentaireChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      [code]: { ...prev[code], commentaire: value }
    }));
  };

  const handleValeurChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      [code]: { ...prev[code], valeur: value }
    }));
  };

  const handleValeurInterieurChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      [code]: { ...prev[code], valeur_interieur: value }
    }));
  };

  const handleValeurExterieurChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      [code]: { ...prev[code], valeur_exterieur: value }
    }));
  };

  const handleImageUpload = async (code, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Vérifier la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5MB)');
      return;
    }

    try {
      setUploadingImage(true);
      setCurrentUploadCode(code);

      const result = await api.uploadChecklistImage(file, id, code);
      
      // Ajouter l'image à la liste
      setFormData(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          images: [...(prev[code]?.images || []), {
            url: result.url,
            filename: result.filename,
            uploadedAt: new Date().toISOString()
          }]
        }
      }));

      toast.success('Image ajoutée');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.message || 'Erreur lors de l\'upload');
    } finally {
      setUploadingImage(false);
      setCurrentUploadCode(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async (code, imageIndex) => {
    const image = formData[code]?.images?.[imageIndex];
    if (!image) return;

    try {
      // Supprimer du serveur
      await api.deleteChecklistImage(image.filename).catch(() => {});
      
      // Supprimer de la liste locale
      setFormData(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          images: prev[code].images.filter((_, i) => i !== imageIndex)
        }
      }));

      toast.success('Image supprimée');
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.error('Vous devez être connecté');
      return;
    }

    const allowedRoles = ['Admin', 'HSE', 'Manager', 'Maintenance', 'Directeur'];
    if (!allowedRoles.includes(user.role)) {
      toast.error('Vous n\'avez pas les permissions pour remplir cette checklist');
      return;
    }

    try {
      setSaving(true);

      const items = Object.entries(formData).map(([code, data]) => ({
        code,
        conforme: data.conforme,
        commentaire: data.commentaire,
        valeur: data.valeur,
        valeur_interieur: data.valeur_interieur,
        valeur_exterieur: data.valeur_exterieur,
        images: data.images
      }));

      const userName = `${user.prenom || ''} ${user.nom || ''}`.trim() || user.email || 'Utilisateur';

      await api.saveMaterielChecklist(
        id,
        { materiel_id: id, checklist_items: items },
        user.id,
        userName,
        user.role
      );

      toast.success('Checklist sauvegardée avec succès');
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getEtatBadge = (etat) => {
    switch (etat) {
      case 'obligatoire':
        return <Badge className="bg-red-100 text-red-800">Obligatoire</Badge>;
      case 'recommande':
        return <Badge className="bg-orange-100 text-orange-800">Recommandé</Badge>;
      case 'optionnel':
        return <Badge className="bg-gray-100 text-gray-800">Optionnel</Badge>;
      default:
        return null;
    }
  };

  const getElementInfo = (code) => {
    return checklistElements.find(e => e.code === code) || {};
  };

  // Calculate stats
  const typeItems = typeChecklist?.checklist_items || [];
  const totalItems = typeItems.length;
  const conformes = Object.values(formData).filter(d => d.conforme === true).length;
  const nonConformes = Object.values(formData).filter(d => d.conforme === false).length;
  const tauxConformite = totalItems > 0 ? Math.round((conformes / totalItems) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!materiel) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto text-orange-500 mb-4" />
        <p className="text-gray-500">Matériel non trouvé</p>
        <Button variant="link" onClick={() => navigate('/materiel')}>
          Retour à la liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={(e) => {
          if (currentUploadCode) {
            handleImageUpload(currentUploadCode, e);
          }
        }}
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/materiel')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7 text-blue-600" />
            Checklist Sécurité - {materiel.code}
          </h1>
          <p className="text-gray-500 mt-1">
            {materiel.designation || materiel.type_materiel_libelle || 'Matériel'}
            {materiel.marque && ` • ${materiel.marque}`}
            {materiel.modele && ` ${materiel.modele}`}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || totalItems === 0}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <ClipboardCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total éléments</p>
                <p className="text-2xl font-bold">{totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Conformes</p>
                <p className="text-2xl font-bold text-green-600">{conformes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Non conformes</p>
                <p className="text-2xl font-bold text-red-600">{nonConformes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Taux de conformité</p>
              <Progress value={tauxConformite} className="h-3 mb-1" />
              <p className="text-xl font-bold">{tauxConformite}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Checklist Items */}
      {totalItems === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-orange-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune checklist définie</h3>
            <p className="text-gray-500 mb-4">
              Le type de matériel "{materiel.type_materiel_libelle}" n'a pas encore de checklist configurée.
            </p>
            <Button variant="outline" onClick={() => navigate('/types-materiel')}>
              Configurer les checklists
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Éléments de sécurité à vérifier</CardTitle>
            <CardDescription>
              Vérifiez chaque élément, ajoutez des commentaires et des photos si nécessaire
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {typeItems.map((item, index) => {
              const elementInfo = getElementInfo(item.code);
              const currentData = formData[item.code] || {};
              const isDualSpeed = elementInfo.value_type === 'dual_speed';
              
              return (
                <div key={item.code} className={`p-4 rounded-lg border ${
                  currentData.conforme === true ? 'bg-green-50 border-green-200' :
                  currentData.conforme === false ? 'bg-red-50 border-red-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="space-y-4">
                    {/* Header row */}
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">
                            {index + 1}. {elementInfo.libelle || ELEMENT_LABELS[item.code] || item.code}
                          </span>
                          {getEtatBadge(item.etat)}
                        </div>
                        
                        {/* Valeurs pour vitesse (dual) */}
                        {isDualSpeed && (
                          <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-white rounded border">
                            <div>
                              <Label className="text-sm text-gray-600">🏢 Intérieur bâtiment</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="number"
                                  value={currentData.valeur_interieur || item.valeur_interieur || ''}
                                  onChange={(e) => handleValeurInterieurChange(item.code, e.target.value)}
                                  className="w-20 h-8"
                                  placeholder="5"
                                />
                                <span className="text-sm text-gray-500">{elementInfo.value_unit}</span>
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm text-gray-600">🌳 Extérieur</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="number"
                                  value={currentData.valeur_exterieur || item.valeur_exterieur || ''}
                                  onChange={(e) => handleValeurExterieurChange(item.code, e.target.value)}
                                  className="w-20 h-8"
                                  placeholder="10"
                                />
                                <span className="text-sm text-gray-500">{elementInfo.value_unit}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Valeur simple */}
                        {elementInfo.has_value && !isDualSpeed && (
                          <div className="flex items-center gap-2 mt-2">
                            <Label className="text-sm text-gray-500">Valeur :</Label>
                            <Input
                              type={elementInfo.value_type === 'number' ? 'number' : 'text'}
                              value={currentData.valeur || ''}
                              onChange={(e) => handleValeurChange(item.code, e.target.value)}
                              className="w-24 h-8"
                              placeholder={item.valeur || ''}
                            />
                            <span className="text-sm text-gray-500">{elementInfo.value_unit}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant={currentData.conforme === true ? 'default' : 'outline'}
                          size="sm"
                          className={currentData.conforme === true ? 'bg-green-600 hover:bg-green-700' : ''}
                          onClick={() => handleConformeChange(item.code, true)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Conforme
                        </Button>
                        <Button
                          variant={currentData.conforme === false ? 'default' : 'outline'}
                          size="sm"
                          className={currentData.conforme === false ? 'bg-red-600 hover:bg-red-700' : ''}
                          onClick={() => handleConformeChange(item.code, false)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Non conforme
                        </Button>
                      </div>
                    </div>

                    {/* Commentaire et Images - toujours visible */}
                    <div className="border-t pt-4 mt-2">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-gray-400" />
                        <Label className="text-sm font-medium">Commentaire / Observation</Label>
                      </div>
                      <Textarea
                        placeholder="Ajoutez un commentaire, une observation ou une remarque..."
                        value={currentData.commentaire || ''}
                        onChange={(e) => handleCommentaireChange(item.code, e.target.value)}
                        rows={2}
                        className="mb-3"
                      />

                      {/* Images */}
                      <div className="flex items-center gap-2 mb-2">
                        <Camera className="w-4 h-4 text-gray-400" />
                        <Label className="text-sm font-medium">Photos</Label>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {/* Images existantes */}
                        {currentData.images?.map((image, imgIndex) => (
                          <div key={imgIndex} className="relative group">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
                              alt={`Photo ${imgIndex + 1}`}
                              className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${image.url}`)}
                            />
                            <button
                              onClick={() => handleRemoveImage(item.code, imgIndex)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        
                        {/* Bouton d'ajout */}
                        <button
                          onClick={() => {
                            setCurrentUploadCode(item.code);
                            fileInputRef.current?.click();
                          }}
                          disabled={uploadingImage && currentUploadCode === item.code}
                          className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors"
                        >
                          {uploadingImage && currentUploadCode === item.code ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 mb-1" />
                              <span className="text-xs">Ajouter</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Dernière vérification */}
      {checklistRemplie && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Dernière vérification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span>{checklistRemplie.rempli_par_nom}</span>
                <Badge variant="outline">{checklistRemplie.rempli_par_role}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{new Date(checklistRemplie.date_verification).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique */}
      {historique.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="historique">
            <AccordionTrigger className="px-4">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique des vérifications ({historique.length})
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-2">
                {historique.map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{h.rempli_par}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{h.taux_conformite}%</Badge>
                      <span className="text-sm text-gray-500">
                        {new Date(h.date).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!imagePreview} onOpenChange={() => setImagePreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Aperçu de l'image</DialogTitle>
          </DialogHeader>
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Aperçu"
              className="w-full h-auto max-h-[70vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
